import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal, Dropdown } from 'react-bootstrap'
import Select from 'react-select'

import FilterPagination from 'pages/parts/FilterPagination'
import PeriodPicker from 'pages/parts/PeriodPicker'
import RightbarCheque from 'pages/main/cheques/Rightbar'
import { httpOk, GET } from 'helpers/api'
import { formatMoney, returnSign, formatDate, findKeyFromArrayByValue } from 'helpers/helpers'

// Источник движения баллов -> ключ перевода
const SOURCE_NAMES = {
	'cheque_award': 'points_accrual_by_cheque',
	'payment_award': 'points_accrual_by_payment',
	'debt_redeem': 'points_debt_redeem',
	'cheque_payment': 'points_payment_by_cheque',
	'reversal': 'points_cheque_reversal',
	'registration': 'points_registration_bonus',
	'manual_award': 'points_manual_accrual',
	'manual_write_off': 'points_manual_write_off',
	'other': 'other',
}

// Окно одно на гостя и на организацию (кэшбэк B2B): организация приходит пропом `organization`,
// её история берётся из своего эндпоинта в том же формате ответа
function LoyaltyHistory({ showModal, setShowModal, client = {}, organization = null }) {
	const { t } = useTranslation()

	const owner = organization ?? client

	const [data, setData] = useState({})
	const [selectedItem, setSelectedItem] = useState({})
	const [showRightBar, setShowRightBar] = useState(false)
	const [pagination, setPagination] = useState({ url: '', response: null })
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterData, setFilterData] = useState({
		start_date: '',
		end_date: '',
		loyalty_service_id: null,
	})
	const [appliedFilterData, setAppliedFilterData] = useState({})

	const ownerData = organization ? data?.organization : data?.client

	async function searchWithFilter(filterDataCopy = filterData) {
		if (!owner.id) return

		var urlParams = ""
		urlParams += returnSign(urlParams) + (organization ? 'organization_id=' : 'client_id=') + owner.id
		if (filterDataCopy.loyalty_service_id)
			urlParams += returnSign(urlParams) + 'loyalty_service_id=' + filterDataCopy.loyalty_service_id
		if (filterDataCopy.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterDataCopy.start_date
		if (filterDataCopy.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterDataCopy.end_date
		if (filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page

		const filterUrl = (organization ? '/organizations-loyalty-histories' : '/clients/loyalty-histories') + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setAppliedFilterData(filterDataCopy)
			setPagination({ 'url': filterUrl, 'response': response.data })
		}
	}

	// Фильтр меняет выборку — страницу сбрасываем, иначе можно остаться на пустой
	function applyFilters() {
		var filterDataCopy = { ...filterData, 'page': null }
		setFilterData(filterDataCopy)
		searchWithFilter(filterDataCopy)
		setDropdownIsOpen(false)
	}

	function paginate(page) {
		var filterDataCopy = { ...filterData, 'page': page }
		setFilterData(filterDataCopy)
		searchWithFilter(filterDataCopy)
	}

	function sourceName(item) {
		return SOURCE_NAMES[item.source] ? t(SOURCE_NAMES[item.source]) : t('other')
	}

	// Из истории открываем сам чек: вопрос всегда один — «с какого чека эти баллы»
	async function toggleRightbarCheque(boolean = false, item = {}) {
		if (!boolean) {
			setSelectedItem({})
			setShowRightBar(false)
			return
		}

		if (!item.cheque_id) return

		const response = await GET(`/cheques/${item.cheque_id}`, {}, { loader: true })
		if (httpOk(response)) {
			setSelectedItem(response.data.data)
			setShowRightBar(true)
		}
	}

	useEffect(() => {
		if (showModal) {
			var filterDataCopy = { start_date: '', end_date: '', loyalty_service_id: null }
			setFilterData(filterDataCopy)
			searchWithFilter(filterDataCopy)
		}
	}, [showModal, owner.id]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<Modal show={showModal} animation={false} centered size="xl"
				onHide={() => setShowModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t('loyalty_history')}
						{organization ?
							<>
								{organization.name && <span className="ms-2">{organization.name}</span>}
								{organization.phone && <span className="ms-2">{organization.phone}</span>}
							</>
							:
							<>
								{client.first_name && <span className="ms-2">{client.first_name}</span>}
								{client.phone1 && <span className="ms-2">{client.phone1}</span>}
							</>
						}
					</Modal.Title>
				</Modal.Header>
				{/* Фиксированная высота, скролл внутри таблицы — как в остальных окнах кабинета */}
				<Modal.Body style={{ 'height': '500px', 'overflow': 'hidden' }}>
					<div className="d-flex mb-2">
						<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
							<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
								{t('filter')}
							</Dropdown.Toggle>

							<Dropdown.Menu className="dropdown-filter">
								<div className="row">
									<div className="form-group col-md-12">
										<label>{t('loyalty')}</label>
										<Select
											isClearable
											options={data.services ?? []}
											value={(data.services ?? []).find(option => option.id === filterData.loyalty_service_id) || ''}
											onChange={(option) => setFilterData({ ...filterData, 'loyalty_service_id': option ? option.id : null })}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									</div>
								</div>
								<div className="row">
									<div className="form-group col-md-12">
										<label>{t('period')}</label>
										<PeriodPicker
											withTime={false}
											startDate={filterData.start_date}
											endDate={filterData.end_date}
											onChange={(period) => setFilterData({ ...filterData, ...period })}
										/>
									</div>
								</div>
								<div className="row mt-3">
									<div className="col-md-12">
										<div className="d-flex justify-content-end gap-2">
											<button className="btn btn-secondary"
												onClick={() => setDropdownIsOpen(false)}>
												{t('cancel')}
											</button>
											<button className="btn btn-primary"
												onClick={() => applyFilters()}>
												{t('apply')}
											</button>
										</div>
									</div>
								</div>
							</Dropdown.Menu>
						</Dropdown>
					</div>

					{/* Что применено — видно без открытия фильтра, как в остальных отчётах */}
					{(appliedFilterData.loyalty_service_id || appliedFilterData.start_date || appliedFilterData.end_date) &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{appliedFilterData.loyalty_service_id &&
									<span className="me-2">
										<b>{t('loyalty')}: </b>
										{findKeyFromArrayByValue(data.services ?? [], 'id', appliedFilterData.loyalty_service_id, 'name')}
									</span>
								}
								{(appliedFilterData.start_date || appliedFilterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{appliedFilterData.start_date &&
											<span>{formatDate(appliedFilterData.start_date)}</span>
										}
										{(appliedFilterData.start_date && appliedFilterData.end_date) &&
											<span>|</span>
										}
										{appliedFilterData.end_date &&
											<span>{formatDate(appliedFilterData.end_date)}</span>
										}
									</span>
								}
							</div>
						</div>
					}

					<div className="d-flex flex-wrap gap-3 mb-2">
						<div><b>{t('balance2')}:</b> <span>{formatMoney(ownerData?.loyalty_balance)}</span></div>
						{/* Накопленная сумма есть только у гостя: у организации объём считается по продажам */}
						{!organization &&
							<div><b>{t('loyalty_overall')}:</b> <span>{formatMoney(ownerData?.loyalty_balance_up)}</span></div>
						}
						<div><b>{t('accrued')}:</b> <span className="text-success">{formatMoney(data?.total_in)}</span></div>
						<div><b>{t('write_off')}:</b> <span className="text-danger">{formatMoney(data?.total_out)}</span></div>
					</div>

					<div className="table-responsive table-fixed" style={{ 'height': '290px', 'overflow': 'auto' }}>
						<table className="table table-bordered table-hover cursor mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '20%' }}>{t('source')}</th>
									<th style={{ 'width': '20%' }}>{t('loyalty')}</th>
									<th style={{ 'width': '10%' }}>{t('cheque')}</th>
									<th style={{ 'width': '15%' }}>{t('pos')}</th>
									<th style={{ 'width': '15%' }}>{t('cashier')}</th>
									<th style={{ 'width': '10%' }} className="text-end text-nowrap">{t('amount')}</th>
									<th style={{ 'width': '10%' }} className="text-end text-nowrap">{t('balance2')}</th>
									<th style={{ 'minWidth': '130px' }} className="text-center text-nowrap">{t('date')}</th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr key={index} onClick={() => toggleRightbarCheque(true, item)}>
										<td className={`text-center ${item.transaction_type === 'plus' ? 'bg-success text-white' : 'bg-danger text-white'}`}>
											{index + 1}
										</td>
										<td className="td-ellipsis" title={sourceName(item)}>
											{sourceName(item)}
											{/* Процент начисления объясняет сумму: за что дали именно столько */}
											{!!item.award_percentage &&
												<span className="ms-1 text-muted">
													({formatMoney(item.award_percentage)}%{item.award_source === 'personal' ? `, ${t('personal_percent_short')}` : ''})
												</span>
											}
										</td>
										<td className="td-ellipsis" title={item.loyalty_service_name}>{item.loyalty_service_name}</td>
										<td className="td-ellipsis text-primary"
											title={item.cheque_number ? `№${item.cheque_number}` : ''}>
											{item.cheque_number ? `№${item.cheque_number}` : ''}
										</td>
										<td className="td-ellipsis">{item.pos_name}</td>
										<td className="td-ellipsis">{item.cashier_name ? item.cashier_name : item.cashier_login}</td>
										<td className={`text-end ${item.transaction_type === 'plus' ? 'text-success' : 'text-danger'}`}>
											{item.transaction_type === 'plus' ? '+' : '-'}{formatMoney(item.amount)}
										</td>
										<td className="text-end">{formatMoney(item.balance_now)}</td>
										<td className="text-center">{formatDate(item.created_at, 'dd.MM.yy HH:mm:ss')}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={(page) => paginate(page)}>
						</FilterPagination>
					}
				</Modal.Body>
			</Modal>

			{showRightBar &&
				<RightbarCheque
					showRightBar={showRightBar}
					setShowRightBar={setShowRightBar}
					selectedItem={selectedItem}>
				</RightbarCheque>
			}
		</>
	)
}

export default LoyaltyHistory
